import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { api, queryString } from '@/lib/api';
import type { StatusTone } from '@/components/ui/status-badge';
import type {
  AdminChangeRequestView,
  ApproveChangeRequestInput,
  DeclineChangeRequestInput,
} from '@shared/schemas/change-request';
import type {
  AdminJobRequestView,
  ApproveJobRequestInput,
  DeclineJobRequestInput,
  JobRequestStatusValue,
} from '@shared/schemas/job-request';
import type { ListResponse, RecordSort } from '@shared/schemas/pagination';

/* The queries behind /admin/job-requests. ROADMAP.md §5.5. */

const ROOT = ['admin', 'job-requests'] as const;

/**
 * The badge tone per state — the one mapping, so no screen invents its own.
 *
 * `declined` is `cancelled`-toned rather than red-alarming: a decline is a
 * normal answer to a normal question, not a failure.
 */
export const JOB_REQUEST_TONES: Record<JobRequestStatusValue, StatusTone> = {
  pending: 'pending',
  approved: 'completed',
  declined: 'cancelled',
};

/**
 * Approving creates a **job**, so the jobs and calendar caches are stale too —
 * otherwise the newly booked clean is missing from the calendar until a reload,
 * which reads as the approval not having worked.
 */
function invalidate(queryClient: QueryClient): Promise<void> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ROOT }),
    queryClient.invalidateQueries({ queryKey: ['admin', 'jobs'] }),
  ]).then(() => undefined);
}

export interface AdminJobRequestListParams {
  sort: RecordSort;
  page: number;
  status: JobRequestStatusValue | 'all';
}

export interface AdminJobRequestList extends ListResponse<AdminJobRequestView> {
  counts: Record<JobRequestStatusValue | 'all', number>;
}

export function useJobRequests(params: AdminJobRequestListParams) {
  return useQuery({
    queryKey: [...ROOT, 'list', params],
    queryFn: () =>
      api.get<AdminJobRequestList>(
        `/api/admin/job-requests${queryString({ page: params.page, sort: params.sort, status: params.status })}`,
      ),
    placeholderData: (previous) => previous,
  });
}

export function useApproveJobRequest(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ApproveJobRequestInput) =>
      api.post<AdminJobRequestView>(`/api/admin/job-requests/${requestId}/approve`, input),
    onSuccess: () => invalidate(queryClient),
  });
}

export function useDeclineJobRequest(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: DeclineJobRequestInput) =>
      api.post<AdminJobRequestView>(`/api/admin/job-requests/${requestId}/decline`, input),
    onSuccess: () => invalidate(queryClient),
  });
}

/* Change requests — ROADMAP.md §5.6. */

export interface AdminChangeRequestList extends ListResponse<AdminChangeRequestView> {
  counts: Record<'pending' | 'approved' | 'declined' | 'all', number>;
}

/**
 * Confirming a change **moves a job**, so the jobs and calendar caches go stale
 * with it — the same reason approving a request does.
 */
export function useChangeRequests(params: { page: number; status: 'pending' | 'all' }) {
  return useQuery({
    queryKey: ['admin', 'change-requests', params],
    queryFn: () =>
      api.get<AdminChangeRequestList>(
        `/api/admin/change-requests${queryString({ page: params.page, status: params.status })}`,
      ),
    placeholderData: (previous) => previous,
  });
}

function invalidateChange(queryClient: QueryClient): Promise<void> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ['admin', 'change-requests'] }),
    queryClient.invalidateQueries({ queryKey: ['admin', 'jobs'] }),
  ]).then(() => undefined);
}

export function useApproveChangeRequest(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ApproveChangeRequestInput) =>
      api.post<AdminChangeRequestView>(`/api/admin/change-requests/${requestId}/approve`, input),
    onSuccess: () => invalidateChange(queryClient),
  });
}

export function useDeclineChangeRequest(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: DeclineChangeRequestInput) =>
      api.post<AdminChangeRequestView>(`/api/admin/change-requests/${requestId}/decline`, input),
    onSuccess: () => invalidateChange(queryClient),
  });
}
